import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Mask helpers ──────────────────────────────────────────────────────────────
const maskCnpj = v => v.replace(/\D/g,'').slice(0,14)
  .replace(/(\d{2})(\d)/,'$1.$2').replace(/(\d{3})(\d)/,'$1.$2')
  .replace(/(\d{3})(\d)/,'$1/$2').replace(/(\d{4})(\d)/,'$1-$2');
const maskCpf = v => v.replace(/\D/g,'').slice(0,11)
  .replace(/(\d{3})(\d)/,'$1.$2').replace(/(\d{3})(\d)/,'$1.$2')
  .replace(/(\d{3})(\d{1,2})$/,'$1-$2');

function applyPhoneMask(digits, mask) {
  if (!digits || !mask) return digits || "";
  const d = digits.replace(/\D/g, "");
  let result = ""; let di = 0;
  for (let i = 0; i < mask.length && di < d.length; i++) {
    result += mask[i] === "#" ? d[di++] : mask[i];
  }
  return result;
}
function stripPhone(value) { return (value || "").replace(/\D/g, ""); }

// ── Field renderer ────────────────────────────────────────────────────────────
function PublicField({ field, value, onChange, primary, radius, phoneSettings = {} }) {
  const inputClass = "w-full text-sm px-3 py-2.5 border border-slate-200 outline-none transition-colors bg-white";
  const style = r => ({ borderRadius: r });
  const focus = e => { e.target.style.borderColor = primary; };
  const blur  = e => { e.target.style.borderColor = "#e2e8f0"; };
  const t = field.type || "text";
  const phoneMask = phoneSettings.phone_mask || "(##) #####-####";
  const maxPhoneDigits = (phoneMask.match(/#/g) || []).length;
  // Boolean / Checkbox → label above, toggle below, vertically centered
  if (t === "boolean" || t === "checkbox") {
    const checked = value === true || value === "true";
    return (
      <div className="flex items-center h-9">
        <button type="button" onClick={() => onChange(!checked)}
          className="relative rounded-full transition-colors overflow-hidden flex-shrink-0"
          style={{ backgroundColor: checked ? primary : "#d1d5db", height: 28, width: 52, minWidth: 52 }}>
          <span className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-sm transition-all duration-200"
            style={{ left: checked ? 26 : 4 }} />
        </button>
      </div>
    );
  }

  // Textarea
  if (t === "textarea") {
    return (
      <textarea value={value || ""} onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder} rows={3} required={field.required}
        className={`${inputClass} resize-none`} style={style(radius)} onFocus={focus} onBlur={blur} />
    );
  }

  // Select / options (single) — also render as select if options exist
  if ((t === "select" || t === "options" || (field.options?.length > 0 && t !== "boolean" && t !== "checkbox")) && !field.select_multiple && field.options?.length > 0) {
    return (
      <select value={value || ""} onChange={e => onChange(e.target.value)}
        required={field.required}
        className={inputClass} style={style(radius)}>
        <option value="">Selecionar...</option>
        {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }

  // Multiselect
  if ((t === "select" || t === "options") && field.select_multiple && field.options?.length > 0) {
    const selected = Array.isArray(value) ? value : (value ? [value] : []);
    return (
      <div className="space-y-1.5">
        {field.options.map(opt => (
          <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={selected.includes(opt)}
              onChange={e => {
                const next = e.target.checked ? [...selected, opt] : selected.filter(s => s !== opt);
                onChange(next);
              }}
              className="rounded" style={{ accentColor: primary }} />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  // Currency
  if (t === "currency") {
    const symbol = field.currency_symbol || "R$";
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">{symbol}</span>
        <input type="text" value={value || ""} required={field.required}
          placeholder="0,00"
          onChange={e => {
            let v = e.target.value.replace(/[^\d]/g, "");
            if (!v) { onChange(""); return; }
            let num = parseInt(v, 10) / 100;
            onChange(num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
          }}
          className={`${inputClass} pl-9`} style={style(radius)} onFocus={focus} onBlur={blur} />
      </div>
    );
  }

  // CNPJ
  if (t === "cnpj") {
    return <input type="text" value={value || ""} onChange={e => onChange(maskCnpj(e.target.value))}
      placeholder="00.000.000/0000-00" maxLength={18} required={field.required}
      className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />;
  }

  // CPF
  if (t === "cpf") {
    return <input type="text" value={value || ""} onChange={e => onChange(maskCpf(e.target.value))}
      placeholder="000.000.000-00" maxLength={14} required={field.required}
      className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />;
  }

  // Date / Datetime
  if (t === "date") {
    return <input type="date" value={value || ""} onChange={e => onChange(e.target.value)}
      required={field.required} className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />;
  }
  if (t === "datetime") {
    return <input type="datetime-local" value={value || ""} onChange={e => onChange(e.target.value)}
      required={field.required} className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />;
  }

  // Number
  if (t === "number") {
    return <input type="number" step="any" value={value || ""} onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder} required={field.required}
      className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />;
  }

  // Default: text / email / phone (with mask) / tel
  if (t === "phone" || t === "tel" || t === "celular") {
    const displayVal = value ? applyPhoneMask(value, phoneMask) : "";
    return (
      <input type="tel" value={displayVal}
        onChange={e => {
          const digits = stripPhone(e.target.value).slice(0, maxPhoneDigits);
          onChange(digits);
        }}
        placeholder={applyPhoneMask("0".repeat(maxPhoneDigits), phoneMask)}
        required={field.required}
        className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />
    );
  }

  const htmlType = t === "email" ? "email" : "text";
  return (
    <input type={htmlType} value={value || ""} onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder} required={field.required}
      className={inputClass} style={style(radius)} onFocus={focus} onBlur={blur} />
  );
}

export default function PublicFormPage() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/forms/${slug}`)
      .then(res => { setForm(res.data); setLoading(false); })
      .catch(() => { setError("Formulário não encontrado ou inativo."); setLoading(false); });
  }, [slug]);
  useEffect(() => {
    if (!form?.visual?.font) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(form.visual.font)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, [form?.visual?.font]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    for (const field of form.fields) {
      if (!field.required) continue;
      const v = values[field.field_name];
      const t = field.type || "text";
      const isEmpty =
        t === "boolean" || t === "checkbox" ? false   // toggles are never "empty"
        : Array.isArray(v) ? v.length === 0
        : (v === null || v === undefined || String(v).trim() === "");
      if (isEmpty) {
        setError(`O campo "${field.label}" é obrigatório.`); return;
      }
    }
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/public/forms/${slug}/submit`, { ...values, _hp: "" });
      setSubmitted(true);
      if (form.success.redirect_url) {
        setTimeout(() => { window.location.href = form.success.redirect_url; }, 2000);
      }
    } catch {
      setError("Erro ao enviar. Tente novamente.");
    }
    setSubmitting(false);
  };

  const font = form?.visual?.font || "Inter";
  const bg = form?.visual?.bg_color || "#f8fafc";
  const cardBg = form?.visual?.card_color || "#ffffff";
  const primary = form?.visual?.primary_color || "#3B82F6";
  const radius = form?.visual?.border_radius ?? 8;
  const btnText = form?.visual?.button_text || "Enviar";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}>
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-8" style={{ background: bg }}>
        <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center">
          <span className="text-2xl">⚠️</span>
        </div>
        <p className="text-slate-600 font-medium text-center">{error}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: bg, fontFamily: font }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center shadow-lg" style={{ background: cardBg }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${primary}20` }}>
            <CheckCircle className="w-7 h-7" style={{ color: primary }} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">{form.success.title}</h2>
          <p className="text-slate-500 text-sm">{form.success.message}</p>
          {form.success.redirect_url && (
            <p className="text-xs text-slate-400 mt-4">Você será redirecionado em instantes...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: bg, fontFamily: font }}>
      {/* Banner */}
      {form.visual.banner_url && (
        <div className="w-full h-40 sm:h-56 overflow-hidden">
          <img src={form.visual.banner_url} alt="banner" className="w-full h-full object-cover"
            onError={e => e.target.parentElement.style.display = "none"} />
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Logo */}
        {form.visual.logo_url && (
          <div className="mb-5 flex justify-center">
            <img src={form.visual.logo_url} alt="logo" className="h-12 object-contain"
              onError={e => e.target.style.display = "none"} />
          </div>
        )}

        {/* Form card */}
        <div className="rounded-2xl p-6 sm:p-8 shadow-lg" style={{ background: cardBg }}>
          {/* Title */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">{form.title}</h1>
            {form.subtitle && <p className="text-slate-500 mt-1.5 text-sm">{form.subtitle}</p>}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Honeypot */}
            <input type="text" name="_hp" style={{ display: "none" }} tabIndex={-1} autoComplete="off"
              onChange={e => setValues(p => ({ ...p, _hp: e.target.value }))} />

            <div className="grid grid-cols-2 gap-4 mb-5">
              {form.fields.map((field, i) => (
                  <div key={i} className={field.width === "50" ? "col-span-1" : "col-span-2"}>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {field.label}
                      {field.required && <span className="text-rose-500 ml-0.5">*</span>}
                    </label>
                    <PublicField
                      field={field}
                      value={values[field.field_name]}
                      onChange={v => setValues(p => ({ ...p, [field.field_name]: v }))}
                      primary={primary}
                      radius={radius}
                      phoneSettings={form.phone_settings || {}}
                    />
                  </div>
              ))}
            </div>

            {error && (
              <div className="mb-4 px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: primary, borderRadius: radius }}>
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Enviando..." : btnText}
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">Powered by Pipe4</p>
      </div>
    </div>
  );
}
